export const HEX_COLOR_REGEX = /^#?[0-9a-fA-F]{6}$/;

export function isValidHexColor(value: string): boolean {
    return HEX_COLOR_REGEX.test(value.trim());
}

export function normalizeHexColor(value: string): string {
    const trimmed = value.trim();
    return trimmed.startsWith("#")
        ? trimmed.toLowerCase()
        : `#${trimmed.toLowerCase()}`;
}

interface RgbColor {
    red: number;
    green: number;
    blue: number;
}

function parseHexRgb(hex: string): RgbColor | null {
    const match = /^#([0-9a-f]{6})$/.exec(normalizeHexColor(hex));
    if (!match) {
        return null;
    }
    const value = match[1];
    return {
        red: parseInt(value.slice(0, 2), 16),
        green: parseInt(value.slice(2, 4), 16),
        blue: parseInt(value.slice(4, 6), 16),
    };
}

// Renders a hex color as an rgba() string at the given alpha. Falls back to
// opaque black for an invalid hex so a bad value degrades to visible text
// rather than silently disappearing.
export function hexToRgba(hex: string, alpha: number): string {
    const rgb = parseHexRgb(hex);
    if (!rgb) {
        return `rgba(0, 0, 0, ${alpha})`;
    }
    return `rgba(${rgb.red}, ${rgb.green}, ${rgb.blue}, ${alpha})`;
}

const LIGHT_CONTRAST_TEXT_COLOR = "#ffffff";
const DARK_CONTRAST_TEXT_COLOR = "#111827";

function toLinearChannel(channel: number): number {
    const scaled = channel / 255;
    return scaled <= 0.03928
        ? scaled / 12.92
        : ((scaled + 0.055) / 1.055) ** 2.4;
}

// WCAG relative luminance: https://www.w3.org/TR/WCAG21/#dfn-relative-luminance
function getRelativeLuminance({ red, green, blue }: RgbColor): number {
    return (
        0.2126 * toLinearChannel(red) +
        0.7152 * toLinearChannel(green) +
        0.0722 * toLinearChannel(blue)
    );
}

// WCAG contrast ratio: https://www.w3.org/TR/WCAG21/#dfn-contrast-ratio
function getContrastRatio(first: RgbColor, second: RgbColor): number {
    const firstLuminance = getRelativeLuminance(first);
    const secondLuminance = getRelativeLuminance(second);
    return (
        (Math.max(firstLuminance, secondLuminance) + 0.05) /
        (Math.min(firstLuminance, secondLuminance) + 0.05)
    );
}

// Picks white or near-black text, whichever has the higher contrast ratio on
// the given background. Falls back to near-black for an invalid hex.
export function getContrastingTextColor(backgroundHex: string): string {
    const background = parseHexRgb(backgroundHex);
    const lightText = parseHexRgb(LIGHT_CONTRAST_TEXT_COLOR);
    const darkText = parseHexRgb(DARK_CONTRAST_TEXT_COLOR);
    if (!background || !lightText || !darkText) {
        return DARK_CONTRAST_TEXT_COLOR;
    }
    return getContrastRatio(background, lightText) >
        getContrastRatio(background, darkText)
        ? LIGHT_CONTRAST_TEXT_COLOR
        : DARK_CONTRAST_TEXT_COLOR;
}

// Hue in degrees (0–360), saturation and value as percentages (0–100). Kept
// unrounded so a hex → HSV → hex round trip returns the original color; round
// only for display.
export interface HsvColor {
    hue: number;
    saturation: number;
    value: number;
}

interface ClampRange {
    value: number;
    min: number;
    max: number;
}

function clamp({ value, min, max }: ClampRange): number {
    return Math.min(max, Math.max(min, value));
}

function expandShortHexColor(hex: string): string {
    const normalized = normalizeHexColor(hex);
    const digits = normalized.slice(1);
    if (digits.length === 3 && /^[0-9a-f]{3}$/.test(digits)) {
        return `#${digits
            .split("")
            .map((digit) => `${digit}${digit}`)
            .join("")}`;
    }
    return normalized;
}

export function hexToHsv(hex: string): HsvColor {
    const normalized = expandShortHexColor(hex).slice(1);
    if (!isValidHexColor(`#${normalized}`)) {
        return { hue: 0, saturation: 0, value: 0 };
    }
    const red = parseInt(normalized.slice(0, 2), 16) / 255;
    const green = parseInt(normalized.slice(2, 4), 16) / 255;
    const blue = parseInt(normalized.slice(4, 6), 16) / 255;
    const max = Math.max(red, green, blue);
    const delta = max - Math.min(red, green, blue);

    let hue = 0;
    if (delta !== 0) {
        if (max === red) {
            hue = 60 * (((green - blue) / delta) % 6);
        } else if (max === green) {
            hue = 60 * ((blue - red) / delta + 2);
        } else {
            hue = 60 * ((red - green) / delta + 4);
        }
    }

    return {
        hue: hue < 0 ? hue + 360 : hue,
        saturation: max === 0 ? 0 : (delta / max) * 100,
        value: max * 100,
    };
}

export function hsvToHex({ hue, saturation, value }: HsvColor): string {
    const normalizedHue = ((hue % 360) + 360) % 360;
    const normalizedSaturation =
        clamp({ value: saturation, min: 0, max: 100 }) / 100;
    const normalizedValue = clamp({ value, min: 0, max: 100 }) / 100;
    const chroma = normalizedValue * normalizedSaturation;
    const secondary = chroma * (1 - Math.abs(((normalizedHue / 60) % 2) - 1));
    const offset = normalizedValue - chroma;
    const sector = Math.floor(normalizedHue / 60) % 6;
    const [red, green, blue] = [
        [chroma, secondary, 0],
        [secondary, chroma, 0],
        [0, chroma, secondary],
        [0, secondary, chroma],
        [secondary, 0, chroma],
        [chroma, 0, secondary],
    ][sector];

    return `#${[red, green, blue]
        .map((channel) =>
            Math.round((channel + offset) * 255)
                .toString(16)
                .padStart(2, "0"),
        )
        .join("")}`;
}
